import { Recipe } from '../types/mealPlan';
import { PROTEIN_KEY_DISPLAY } from './proteinSuggestions';
import { ingredientChipId, selectIngredientChips } from './ingredientChips';

export type FeedbackTagGroup = 'flavor' | 'recipe';

// The moment the feedback is given: 'rating' is post-cooking experience
// feedback (thumbs down); 'swap' is planning-time rejection (swap sheet).
// Experience-only tags (e.g. "not seasoned enough") never show at swap time.
export type FeedbackContext = 'rating' | 'swap';

/**
 * Whether a chip earns a slot in the collapsed swap sheet.
 *
 * Set from what people actually tap. Across a year of swaps, 72% of all taps
 * were the three attribute-rejection chips (protein 8, cuisine 5, format 5),
 * and the flavor and texture chips — too_spicy, too_sweet, too_tangy,
 * too_smoky, too_saucy, too_creamy — recorded ZERO between them. Users reach
 * for these to say "this is the wrong thing", not "this is too something".
 *
 * The same data says nobody has ever picked more than 3 chips and 91% of
 * chip-users pick one or two, so a 13-chip sheet was offering breadth nobody
 * used at the cost of making the useful chips harder to find.
 *
 * Caveat for whoever retunes this: raw tap counts flatter the primaries,
 * because protein/cuisine/format are offered on nearly every recipe while
 * too_smoky only shows on smoky dishes. Chip impressions aren't logged, so
 * taps-per-impression isn't computable yet. Rank order within `secondary` is
 * noise at this sample size.
 */
export type FeedbackTagTier = 'primary' | 'secondary';

export type FeedbackTag = {
  id: string;
  label: string;
  group: FeedbackTagGroup;
  tier: FeedbackTagTier;
  dietaryViolation?: string; // The specific restriction violated (e.g. "Gluten-Free")
};

export type FeedbackTagOptions = {
  context?: FeedbackContext; // defaults to 'rating' (existing behavior)
  kids?: number; // household kid count; gates the kids chip
  /**
   * Terms the user already excludes (dislikes, allergies, avoided proteins).
   * Suppresses ingredient chips for things they have already told us about.
   */
  avoidTerms?: string[];
};

export const FEEDBACK_GROUP_LABELS: Record<FeedbackTagGroup, string> = {
  flavor: 'Flavor',
  recipe: 'Recipe',
};

/** Tier is filled in at the end of getRecipeFeedbackTags, so builders omit it. */
type DraftFeedbackTag = Omit<FeedbackTag, 'tier'> & { tier?: FeedbackTagTier };

/**
 * The chips that earn a slot before "More reasons". Everything not listed
 * here, and every ingredient chip past the second, is secondary.
 *
 * These plus ingredient chips accounted for ~88% of observed taps.
 * `had_recently` and `kids_wont_eat` are here on lower evidence (1 and 2 taps)
 * but survive because neither is inferable from the recipe: only the user
 * knows what they ate on Tuesday or what their kids will refuse.
 */
const PRIMARY_TAG_IDS = new Set([
  'dislike_protein',
  'dislike_cuisine',
  'dislike_format',
  'kids_wont_eat',
  'had_recently',
]);

/**
 * How many ingredient chips are worth a primary slot. selectIngredientChips
 * still returns up to MAX_INGREDIENT_CHIPS; the rest fall to "More reasons",
 * so an ingredient-heavy dish can't crowd out the household chips.
 */
const DEFAULT_VISIBLE_INGREDIENT_CHIPS = 2;

const FORMAT_DISPLAY_LABELS: Record<string, string | null> = {
  salad: 'salads',
  soup_stew: 'soups/stews',
  bowl: 'bowls',
  handheld: 'handhelds',
  pasta_noodle: 'pasta',
  casserole_bake: 'casseroles/bakes',
  stir_fry: 'stir fry',
  flatbread_pizza: 'flatbreads/pizza',
  sheet_pan_meal: 'sheet pan meals',
  main_with_side: null, // Too generic to present as feedback
};

/**
 * Ingredient chips a rendered format chip already speaks for.
 *
 * The noodles ARE the pasta_noodle format and the greens ARE the salad, so
 * offering both put two chips reading the same word side by side in the swap
 * sheet: a production session on "Crumbled Tempeh Ragu over Chickpea Penne"
 * showed "Not feeling pasta" next to "Don't want pasta". Same guard as
 * mainProteinKey in selectIngredientChips — one complaint, one chip — and the
 * format chip wins because it's the one that steers the substitute away from
 * being a pasta dish at all, not just away from containing pasta.
 *
 * Deliberately narrow: only pairs that read as the same complaint. A tortilla
 * on a handheld or a pita under a flatbread is a component that could change
 * while the format stays, so those chips still say different things and both
 * are offered. Noodle keys other than `pasta` are left alone for the same
 * reason — "Don't want rice noodles" is a distinguishable ask, and calling a
 * pad thai "pasta" is a format-label problem, not a duplication one.
 */
const FORMAT_COVERED_INGREDIENT_KEYS: Record<string, string[]> = {
  pasta_noodle: ['pasta'],
  salad: ['lettuce'],
};

const NON_AUTHENTIC_EXCLUDED_CUISINES = ['American', 'Tex-Mex'];

// PROTEIN_KEY_DISPLAY (priority-ordered protein subset of the canonical
// ingredient keys) lives in ./proteinSuggestions so the preference autocompletes
// can share it. The first key present in recipe.ingredientKeys is treated as the
// dish's main protein (display only — the backend independently resolves the
// protein when processing the dislike_protein tag).
function detectMainProtein(recipe: Recipe): [string, string] | null {
  const keys = recipe.ingredientKeys;
  if (!keys || keys.length === 0) return null;
  return PROTEIN_KEY_DISPLAY.find(([key]) => keys.includes(key)) ?? null;
}

/**
 * Returns the set of relevant feedback tags for a recipe, grouped by category.
 * Tags adapt to the recipe (only offer "too spicy" on a spicy recipe), the
 * household (only offer the kids chip when there are kids), and the moment
 * (experience-only tags are excluded from planning-time swap feedback).
 */
export function getRecipeFeedbackTags(
  recipe: Recipe,
  userDietaryRestrictions?: string[],
  options?: FeedbackTagOptions
): FeedbackTag[] {
  const context: FeedbackContext = options?.context ?? 'rating';
  const kids = options?.kids ?? 0;
  // Tier is applied in one pass at the end (see PRIMARY_TAG_IDS) rather than
  // repeated across two dozen push sites. Ingredient chips are the exception:
  // theirs depends on position, so they set it inline.
  const tags: DraftFeedbackTag[] = [];
  const recipeTags = recipe.tags?.map((t) => t.toLowerCase()) || [];

  // --- Flavor group ---
  if (recipeTags.includes('spicy')) {
    tags.push({ id: 'too_spicy', label: 'Too spicy', group: 'flavor' });
  }
  if (recipeTags.includes('sweet')) {
    tags.push({ id: 'too_sweet', label: 'Too sweet', group: 'flavor' });
  }
  if (recipeTags.includes('rich')) {
    tags.push({ id: 'too_rich', label: 'Too rich/heavy', group: 'flavor' });
  }
  if (recipeTags.includes('tangy')) {
    tags.push({ id: 'too_tangy', label: 'Too tangy/sour', group: 'flavor' });
  }
  if (recipeTags.includes('smoky')) {
    tags.push({ id: 'too_smoky', label: 'Too smoky', group: 'flavor' });
  }
  if (context === 'rating' && (recipeTags.includes('rich') || recipeTags.includes('savory'))) {
    tags.push({ id: 'too_greasy', label: 'Too greasy', group: 'flavor' });
  }

  // Texture. Only the values that invert into a complaint someone would
  // actually make — nobody says "too crispy", and "too hearty" isn't idiomatic,
  // so crispy/crunchy/hearty are deliberately absent.
  const textures = recipe.textureTags ?? [];
  if (textures.includes('saucy')) {
    tags.push({
      id: 'too_saucy',
      label: context === 'swap' ? 'Not feeling something saucy' : 'Too saucy',
      group: 'flavor',
    });
  }
  if (textures.includes('creamy')) {
    tags.push({
      id: 'too_creamy',
      label: context === 'swap' ? 'Not feeling something creamy' : 'Too creamy',
      group: 'flavor',
    });
  }
  // Rating-only: you can't tell a dish is chewy from a card, and the complaint
  // is usually about how it turned out rather than what it is.
  if (context === 'rating' && textures.includes('chewy')) {
    tags.push({ id: 'too_chewy', label: 'Too chewy', group: 'flavor' });
  }
  if (context === 'rating') {
    tags.push({ id: 'too_bland', label: 'Not seasoned enough', group: 'flavor' });
  }

  // Attribute-rejection chips are SWAP-ONLY.
  //
  // Cuisine, protein and format are all visible before cooking, and the user
  // has already endorsed them more than once by the time they're rating: they
  // had the swap drawer and the alternatives picker, then activated the plan,
  // then cooked it. Someone who activates a plan containing a chicken recipe
  // does not have an aversion to chicken, so "Not a fan of chicken" isn't a
  // coherent thing to offer them afterwards.
  //
  // Removing them post-cook doesn't lose the signal. The RATE_DOWN itself
  // already applies its full weight to every axis the recipe touches, cuisine
  // and protein included; the chip only doubled it (see processFeedbackTags).
  //
  // Labels stay moment-scoped — "not feeling X this week" rather than a claim
  // about durable taste. Repeated swap taps become durable preference through
  // axis accumulation, so the wording doesn't need to overclaim.
  if (context === 'swap') {
    if (recipe.cuisine) {
      tags.push({
        id: 'dislike_cuisine',
        label: `Not feeling ${recipe.cuisine}`,
        group: 'flavor',
      });
    }
    const mainProtein = detectMainProtein(recipe);
    if (mainProtein) {
      tags.push({
        id: 'dislike_protein',
        label: `Don't want ${mainProtein[1]}`,
        group: 'flavor',
      });
    }
    // Named-ingredient rejection. This is the highest-volume complaint people
    // type into the swap sheet's free-text box ("I don't like green beans"),
    // and until now the chip vocabulary had no slot for it: the only ingredient
    // chip was dislike_protein, which reaches one key per dish. A tapped chip
    // attributes the rejection to the ingredient the user names, instead of
    // spreading it evenly over every axis the recipe touches.
    //
    // Offered in BOTH swap modes, unlike dislike_cuisine. "This dish but
    // without the asparagus" is the canonical modify request.
    // Resolved before the ingredient chips because a format chip suppresses the
    // ingredient chip it duplicates, but pushed after them to keep chip order.
    // Only formatTags[0] matters: that's the one the chip is built from, so a
    // pasta_noodle tag sitting in second place isn't being said twice.
    const formatTag = recipe.formatTags?.[0];
    const formatLabel = formatTag ? FORMAT_DISPLAY_LABELS[formatTag] : null;

    const ingredientChips = selectIngredientChips(recipe, {
      mainProteinKey: mainProtein?.[0] ?? null,
      avoidTerms: options?.avoidTerms,
      suppressKeys: formatLabel
        ? FORMAT_COVERED_INGREDIENT_KEYS[formatTag as string]
        : undefined,
    });
    ingredientChips.forEach((ingredient, index) => {
      tags.push({
        id: ingredientChipId(ingredient.key),
        label: `Don't want ${ingredient.display}`,
        group: 'flavor',
        tier:
          index < DEFAULT_VISIBLE_INGREDIENT_CHIPS ? 'primary' : 'secondary',
      });
    });
    if (formatLabel) {
      tags.push({
        id: 'dislike_format',
        label: `Not feeling ${formatLabel}`,
        group: 'flavor',
      });
    }
  }
  // Both moments. Authenticity is judgeable from the card — often more obvious
  // there than after cooking, since a westernised shortcut usually gives itself
  // away in the ingredient list. Users who tailor plans around authentic
  // cooking want this at swap time, when the plan is still being shaped.
  if (
    recipe.cuisine &&
    !NON_AUTHENTIC_EXCLUDED_CUISINES.includes(recipe.cuisine)
  ) {
    tags.push({
      id: 'not_authentic',
      label: context === 'swap' ? 'Want something more authentic' : 'Not authentic',
      group: 'flavor',
    });
  }

  // --- Recipe group ---
  // Swap-only for the same reason as cuisine/protein/format above: the dietary
  // style was on the card when they activated the plan.
  if (context === 'swap') {
    if (recipe.dietaryRestrictions?.includes('Vegetarian')) {
      tags.push({
        id: 'dislike_vegetarian',
        label: "Don't want vegetarian",
        group: 'recipe',
      });
    }
    if (recipe.dietaryRestrictions?.includes('Vegan')) {
      tags.push({
        id: 'dislike_vegan',
        label: "Don't want vegan",
        group: 'recipe',
      });
    }
  }
  if (recipe.difficulty === 'medium' || recipe.difficulty === 'hard') {
    tags.push({ id: 'too_complex', label: 'Too complex', group: 'recipe' });
  }
  if ((recipe.ingredients?.length ?? 0) > 10) {
    tags.push({ id: 'too_many_ingredients', label: 'Too many ingredients', group: 'recipe' });
  }
  if (recipe.totalTimeMinutes > 30) {
    tags.push({ id: 'takes_too_long', label: 'Takes too long', group: 'recipe' });
  }
  // Stays in BOTH moments, unlike the attribute rejections above. The parent
  // can't have endorsed "the kids will eat this" at plan time — they were
  // guessing, and serving it is how they find out. Only the tense moves: a
  // prediction in the swap drawer, a result in the feedback modal.
  if (kids > 0) {
    tags.push({
      id: 'kids_wont_eat',
      label:
        context === 'swap'
          ? "The kids won't eat this"
          : "The kids wouldn't eat it",
      group: 'recipe',
    });
  }
  if (context === 'swap') {
    tags.push({ id: 'had_recently', label: 'Had something like this recently', group: 'recipe' });
  }
  if (context === 'rating') {
    // Servings are judged after cooking; at planning time they're adjustable
    tags.push({ id: 'not_enough_servings', label: 'Not enough servings', group: 'recipe' });
    tags.push({ id: 'too_many_servings', label: 'Too many servings', group: 'recipe' });
  }

  if (context === 'rating' && userDietaryRestrictions?.length && recipe.dietaryRestrictions?.length) {
    const recipeRestrictions = new Set(recipe.dietaryRestrictions);
    for (const restriction of userDietaryRestrictions) {
      if (recipeRestrictions.has(restriction)) {
        tags.push({
          id: `dietary_violation:${restriction}`,
          // Rendered as-is, not lowercased. These are canonical labels shown
          // verbatim everywhere else in the UI (badges, the dietary picker),
          // and lowercasing mangles the acronyms — "Low-FODMAP" came out as
          // "low-fodmap". Same trap cleanDietaryRestrictions hit on the backend.
          label: `Not actually ${restriction}`,
          group: 'recipe',
          dietaryViolation: restriction,
        });
      }
    }
  }

  return tags.map((tag) => ({
    ...tag,
    tier: tag.tier ?? (PRIMARY_TAG_IDS.has(tag.id) ? 'primary' : 'secondary'),
  }));
}
